import { createTheme } from '@mui/material';

export const lightTheme = createTheme({
    palette: {
        primary: {
            main: 'rgb(25, 118, 210)',
        },
        footer: {
            background: '#0b1f2a',
            text: '#B0BEC5',
        },
        navigation: {
            main: '#111828',
        },
        background: {
            default: '#fff',
            paper: '#fff',
            card: '#fff',
            darkCard: '#f5f5f5',
            light: '#374151', // Gris más claro para el fondo de los componentes
            darkContrast: '#384152',
        },
        text: {
            primary: '#263238',
            secondary: '#546e7a',
            disabled: '#9e9e9e',
        },
        border: {
            light: 'rgba(0, 0, 0, 0.05)',
            main: 'rgba(0, 0, 0, 0.1)',
            dark: 'rgba(0, 0, 0, 0.15)',
            darkerLight: 'rgba(0, 0, 0, 0.25)',
            darker: 'rgba(0, 0, 0, 0.5)',
            accent: '#50109e',
            accent2: '#bb86fc',
        },
        secondary: {
            main: '#00793d',
        },
        tertiary: {
            main: '#1587c0',
            light: '#1587c0',
            dark: '#1587c0',
        },
        disabled: {
            main: '#9e9e9e',
        },
        error: {
            main: '#d32f2f',
        },
        warning: {
            main: '#d15b00',
        },
        delete: {
            main: '#fff',
            contrastText: '#d32f2f',
        },
        link: {
            main: '#0d6efd',
        },
        analitica: {
            main: '#22272F',
            contrastText: '#fff',
        },
        tabs: {
            main: '#22272F',
        },
        white: {
            main: '#fff',
        },
        tabsDecorator: {
            main: '#1D6FB1',
        },
        white: {
            main: '#fff',
        },
        black: {
            main: '#000',
        },
        risks: {
            veryHigh: 'rgb(242 127 127)',
            high: 'rgb(208 172 108)',
            medium: '#dddddd',
            low: 'rgb(148 191 148)',
            veryLow: 'rgb(192 216 237)',
        },
        minutes: {
            veryHigh: '#2196f3',
            high: '#0C6',
            medium: '#ccc',
            low: '#F90',
            veryLow: '#d32f2f',
        },
        competitions: {
            laLiga: '#ff4b44',
            fifaFriendlies: '#4db7ad',
            championsLeague: '#1d6fb1',
            supercopaEspaña: '#F66A03',
            copaRey: 'rgb(229 184 105)',
            europaLeague: '#1a892d',
            euroClasificacion: '#158ec0',
            defaultColor: '#69b59f',
        },
    },
    info: {
        success: '#edf7ed',
        warning: '#fdf3ea',
        error: '#fdeded',
        info: '#e5f6fd',
        default: '#f5f5f5',
    },
    boxShadows: {
        main: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',
        hover: '0px 0px 10px 0px rgba(0, 0, 0, 0.2)',
        active: '0px 0px 10px 0px rgba(0, 0, 0, 0.3)',
        disabled: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',
        selected: '0px 0px 10px 0px rgba(0, 0, 0, 0.4)',
    },
    gradient: {
        main: 'linear-gradient(90deg, #e3f2fd 0%, #bbdefb 60%, transparent 100%)',
        secondary: 'linear-gradient(90deg, #e3f2fd 0%, #fce4ec 60%, transparent 100%)',
    },
    mercado: {
        up: '#1a892d',
        down: '#dc4141',
        default: '#000',
    },
    resultados: {
        win: '#4CAF50',
        draw: '#FF9800',
        lose: '#F44336',
    },
    resultadosBox: {
        win: 'rgb(0, 121, 61)',
        draw: 'rgb(212, 90, 0)',
        lose: 'rgb(186, 59, 59)',
    },
    tier: {
        tier1: '#ff4b44',
        tier2: '#4CAF50',
        tier3: '#1a892d',
        tier4: '#158ec0',
        tier5: '#1d6fb1',
        tier6: '#4db7ad',
        tier7: '#69b59f',
    },
    typography: {
        allVariants: {
            fontFamily: `'Roboto Condensed', sans-serif`,
            textTransform: 'none',
            fontSize: 16,
            color: '#000',
        },
        h1: {
            fontSize: '20px',
            fontWeight: 500,
            marginBottom: '1rem',
            lineHeight: 1.2,
        },
        h2: {
            fontSize: '19px',
            fontWeight: 500,
        },
        h3: {
            fontSize: '18px',
            fontWeight: 500,
        },
        h4: {
            fontSize: '17px',
            fontWeight: 500,
        },
        h5: {
            fontSize: '16px',
            fontWeight: 500,
        },
        h6: {
            fontSize: '15px',
            fontWeight: 500,
        },
    },
    components: {
        MuiTableRow: {
            styleOverrides: {
                root: ({ theme }) => ({
                    transition: 'background-color 0.3s ease',
                    '&:hover': {
                        backgroundColor: 'rgba(76, 175, 80, 0.08)', // verde suave
                        cursor: 'pointer',
                    },
                    border: `1px solid ${theme.palette.border.light}`,
                }),
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    '--Paper-shadow': 'none',
                    '--Paper-overlay': 'none',
                    boxShadow: 'none',
                    backgroundImage: 'none',
                    backgroundColor: '#f9f9f9',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: '#64B5F6',
                    textDecoration: 'none',
                    '&:hover': {
                        color: '#1E88E5',
                        textDecoration: 'underline',
                    },
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    a: {
                        color: '#64B5F6',
                        textDecoration: 'none',
                        '&:hover': {
                            color: '#1E88E5',
                            textDecoration: 'underline',
                        },
                    },
                },
            },
        },
        MuiContainer: {
            styleOverrides: {
                root: {
                    paddingLeft: '8px',
                    paddingRight: '8px',
                },
            },
        },
        MuiTableContainer: {
            styleOverrides: {
                root: ({ theme }) => ({
                    position: 'relative',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '8px',
                    '&:before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        height: '3px',
                        background:
                            'linear-gradient(90deg, #69b59f 0%, rgba(105, 181, 159, 0.25) 60%, transparent 100%)',
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                        pointerEvents: 'none',
                        zIndex: 1,
                    },
                }),
            },
        },
    },
});
